'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, XCircle, DollarSign } from 'lucide-react';
import { getBudgetLimits, setBudgetLimits } from '@/app/actions/usage';
import type { BudgetLimits } from '@/lib/usage-types';

export function BudgetSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingError, setSavingError] = useState<string | null>(null);

  // Ref to track if component is mounted (prevents memory leak)
  const isMountedRef = useRef(true);

  // Form state for limits
  const [dailyLimit, setDailyLimit] = useState('');
  const [monthlyLimit, setMonthlyLimit] = useState('');
  const [perChatLimit, setPerChatLimit] = useState('');
  const [alertThreshold, setAlertThreshold] = useState('80');
  const [blockOnExceed, setBlockOnExceed] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const limitsRes = await getBudgetLimits();

      // Check if component is still mounted before updating state
      if (!isMountedRef.current) return;

      if (limitsRes.success && limitsRes.data) {
        const l = limitsRes.data;
        setDailyLimit(l.daily?.toString() || '');
        setMonthlyLimit(l.monthly?.toString() || '');
        setPerChatLimit(l.perChat?.toString() || '');
        setAlertThreshold((l.alertThreshold * 100).toString());
        setBlockOnExceed(l.blockOnExceed);
      }
    } catch (err) {
      console.error('Failed to load budget limits:', err);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    loadData();

    return () => {
      isMountedRef.current = false;
    };
  }, [loadData]);

  const handleSaveLimits = async () => {
    setSaving(true);
    setSavingError(null);
    try {
      const newLimits: Partial<BudgetLimits> = {
        daily: dailyLimit ? parseFloat(dailyLimit) : null,
        monthly: monthlyLimit ? parseFloat(monthlyLimit) : null,
        perChat: perChatLimit ? parseFloat(perChatLimit) : null,
        alertThreshold: parseFloat(alertThreshold) / 100,
        blockOnExceed,
      };

      const result = await setBudgetLimits(newLimits);
      if (result.success) {
        await loadData();
      } else {
        setSavingError(result.error || 'Failed to save limits');
      }
    } catch (err) {
      setSavingError(err instanceof Error ? err.message : 'Failed to save limits');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Budget Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="h-5 w-5" />
          Budget Settings
        </CardTitle>
        <CardDescription>
          Set spending limits and alerts to control your API usage costs.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* 2-column responsive grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="daily">Daily Limit ($)</Label>
            <Input
              id="daily"
              type="number"
              step="0.01"
              placeholder="No limit"
              value={dailyLimit}
              onChange={(e) => setDailyLimit(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="monthly">Monthly Limit ($)</Label>
            <Input
              id="monthly"
              type="number"
              step="0.01"
              placeholder="No limit"
              value={monthlyLimit}
              onChange={(e) => setMonthlyLimit(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="perChat">Per-Chat Limit ($)</Label>
            <Input
              id="perChat"
              type="number"
              step="0.01"
              placeholder="No limit"
              value={perChatLimit}
              onChange={(e) => setPerChatLimit(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="threshold">Alert Threshold (%)</Label>
            <Input
              id="threshold"
              type="number"
              min="0"
              max="100"
              value={alertThreshold}
              onChange={(e) => setAlertThreshold(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Alert when usage reaches this percentage of limit
            </p>
          </div>
        </div>

        {/* Full-width toggle */}
        <div className="flex items-center justify-between max-w-sm">
          <div className="space-y-0.5">
            <Label>Block on Exceed</Label>
            <p className="text-xs text-muted-foreground">
              Prevent new chats when budget exceeded
            </p>
          </div>
          <Switch
            checked={blockOnExceed}
            onCheckedChange={setBlockOnExceed}
          />
        </div>

        {savingError && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertDescription>{savingError}</AlertDescription>
          </Alert>
        )}

        <Button onClick={handleSaveLimits} disabled={saving}>
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Save Limits
        </Button>
      </CardContent>
    </Card>
  );
}
